import { useRef, useState } from "react"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import {
  fieldErrorTextClass,
  fieldInputClass,
  fieldInputErrorClass,
  fieldLabelClass,
  focusRing,
  priceFigureClass,
  priceSuffixClass,
  sectionClass,
} from "../lightTheme"
import { calculatorContent } from "./calculatorContent"
import FigureHalo from "./FigureHalo.jsx"
import { computeLeakage } from "./leakageMath"

const parameterLabelClass = "text-[13px] font-light leading-[18px] text-[#171717]"

const inputCardShadow =
  "shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_2px_2px_0_rgba(0,0,0,0.04),0_8px_16px_-4px_rgba(0,0,0,0.04),0_0_0_1px_#FAFAFA]"

function parseMoneyField(raw) {
  const remainder = String(raw).replace(/[$\s,]/g, "")
  if (remainder === "") return { ok: false }
  if (!/^\d+$/.test(remainder) && !/^\d+\.\d+$/.test(remainder)) {
    return { ok: false }
  }
  const value = Number(remainder)
  if (!Number.isFinite(value) || value < 0) return { ok: false }
  return { ok: true, value }
}

function formatMoney(amount) {
  return `$${Math.round(amount).toLocaleString("en-US")}`
}

function formatLost(lostTenths) {
  const whole = Math.floor(lostTenths / 10)
  const tenth = lostTenths % 10
  return `~${whole}.${tenth}`
}

function sliderPosition(text, fallback, max) {
  const parsed = parseMoneyField(text)
  const amount = parsed.ok ? parsed.value : fallback
  return Math.min(Math.max(0, amount), max)
}

function MoneyField({ id, label, value, sliderValue, min, max, step, error, onChange }) {
  return (
    <div className="flex w-full flex-col gap-3">
      <Label className={fieldLabelClass} htmlFor={id}>
        {label}
      </Label>
      <Input
        id={id}
        type="text"
        inputMode="decimal"
        placeholder=""
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={
          error ? `${fieldInputClass} ${fieldInputErrorClass}` : fieldInputClass
        }
      />
      <input
        id={`${id}-range`}
        type="range"
        min={min}
        max={max}
        step={step}
        value={sliderValue}
        aria-label={label}
        onChange={(event) => onChange(formatMoney(Number(event.target.value)))}
        className={`h-2 w-full cursor-pointer accent-[#171717] ${focusRing}`}
      />
      {error ? <p className={fieldErrorTextClass}>Required</p> : null}
    </div>
  )
}

function MoneyFigure({ amount }) {
  const [lit, setLit] = useState(false)
  const amountRef = useRef(null)
  const text = formatMoney(amount)

  return (
    <span className="flex flex-wrap items-baseline gap-x-1">
      <span
        className="relative inline-block"
        onMouseEnter={() => setLit(true)}
        onMouseLeave={() => setLit(false)}
      >
        <FigureHalo lit={lit} text={text} targetRef={amountRef} />
        <span
          ref={amountRef}
          className={cn(
            priceFigureClass,
            "relative z-10 font-bold max-md:text-[32px] max-md:leading-10"
          )}
        >
          {text}
        </span>
      </span>
      <span className={priceSuffixClass}> / mo</span>
    </span>
  )
}

function CountFigure({ value, unit }) {
  return (
    <p className="text-[24px] font-bold leading-[32px] tracking-[-0.04em] text-[#171717]">
      {value}
      <span className="font-normal text-[#4D4D4D]">{` ${unit}`}</span>
    </p>
  )
}

function MetricCard({ label, children }) {
  return (
    <div className="flex flex-col gap-2 rounded-[2px] border border-solid border-[#E6E6E6] p-6">
      <p className={parameterLabelClass}>{label}</p>
      {children}
    </div>
  )
}

export default function CalculatorSection() {
  const [budgetText, setBudgetText] = useState(calculatorContent.budgetDefault)
  const [contractText, setContractText] = useState(calculatorContent.contractDefault)
  const [budgetError, setBudgetError] = useState(false)
  const [contractError, setContractError] = useState(false)
  const [pair, setPair] = useState({ budget: 5000, contract: 12000 })

  function updateField(field, raw) {
    const nextBudget = field === "budget" ? raw : budgetText
    const nextContract = field === "contract" ? raw : contractText
    if (field === "budget") setBudgetText(raw)
    else setContractText(raw)

    const budgetParsed = parseMoneyField(nextBudget)
    const contractParsed = parseMoneyField(nextContract)
    const selfParsed = field === "budget" ? budgetParsed : contractParsed
    const otherParsed = field === "budget" ? contractParsed : budgetParsed
    const setSelfError = field === "budget" ? setBudgetError : setContractError

    if (!selfParsed.ok) {
      setSelfError(true)
      return
    }
    if (!otherParsed.ok) {
      setSelfError(false)
      return
    }

    const next = computeLeakage(budgetParsed.value, contractParsed.value)
    const finite = [next.leads, next.lostTenths, next.leakage].every(Number.isFinite)
    if (!finite) {
      setSelfError(true)
      return
    }

    setBudgetError(false)
    setContractError(false)
    setPair({ budget: budgetParsed.value, contract: contractParsed.value })
  }

  const result = computeLeakage(pair.budget, pair.contract)

  return (
    <section className={`${sectionClass} translate-x-[calc(50vw-50%-1.5rem)]`}>
      <div className="flex w-full flex-col items-center gap-6 text-center">
        <p className="inline-flex items-center rounded-full border-[2.25px] border-[#171717] bg-[#FAFAFA] px-[10px] py-[4px] text-[13.5px] font-semibold leading-none tracking-[-0.037em] text-[#171717] md:border-[2.8125px] md:text-[19.125px]">
          {calculatorContent.eyebrow}
        </p>
        <h2 className="w-full text-balance text-[40px] font-bold leading-[48px] tracking-[-0.04em] text-[#171717] max-md:text-[28px] max-md:leading-8">
          {calculatorContent.title}
        </h2>
        <p className="max-w-[64ch] text-center text-[18px] font-normal leading-7 text-[#4D4D4D]">
          {calculatorContent.subtitle}
        </p>
      </div>
      <div className="grid w-full grid-cols-1 items-stretch gap-6 md:grid-cols-2">
        <div className={`flex h-full w-full flex-col overflow-hidden rounded-xl bg-white ${inputCardShadow}`}>
          <div className="relative flex h-11 shrink-0 items-center px-4 shadow-[inset_0_-1px_0_rgba(0,0,0,0.08)]">
            <span className="flex items-center gap-1" aria-hidden="true">
              <span className="size-2 rounded-full bg-[#EE6D5E]" />
              <span className="size-2 rounded-full bg-[#F3BF4A]" />
              <span className="size-2 rounded-full bg-[#5DC753]" />
            </span>
            <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 text-[13px] font-normal leading-5 text-[#7D7D7D]">
              {calculatorContent.eyebrow}
            </span>
          </div>
          <div className="flex flex-col gap-6 p-6">
          <MoneyField
            id="ads-budget"
            label={calculatorContent.budgetLabel}
            value={budgetText}
            sliderValue={sliderPosition(
              budgetText,
              pair.budget,
              calculatorContent.budgetSlider.max
            )}
            min={calculatorContent.budgetSlider.min}
            max={calculatorContent.budgetSlider.max}
            step={calculatorContent.budgetSlider.step}
            error={budgetError}
            onChange={(value) => updateField("budget", value)}
          />
          <MoneyField
            id="contract-value"
            label={calculatorContent.contractLabel}
            value={contractText}
            sliderValue={sliderPosition(
              contractText,
              pair.contract,
              calculatorContent.contractSlider.max
            )}
            min={calculatorContent.contractSlider.min}
            max={calculatorContent.contractSlider.max}
            step={calculatorContent.contractSlider.step}
            error={contractError}
            onChange={(value) => updateField("contract", value)}
          />
          </div>
        </div>
        <div className="flex flex-col gap-3 text-left">
          <MetricCard label={calculatorContent.rows.leads}>
            <CountFigure value={`~${result.leads}`} unit="leads/mo" />
          </MetricCard>
          <MetricCard label={calculatorContent.rows.lost}>
            <CountFigure value={formatLost(result.lostTenths)} unit="signed jobs/mo" />
          </MetricCard>
          <MetricCard label={calculatorContent.rows.leakage}>
            <MoneyFigure amount={result.leakage} />
          </MetricCard>
        </div>
      </div>
    </section>
  )
}
